/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
// FLAGGED-FOR-REVIEW-2026-02: This file has residual unused vars / state
// setters / effect-deps that look like DROPPED-FEATURE references, not
// blind-orphan code (state setters never called, computed values never
// rendered, hooks with intentional dep omissions). Reviewer next step:
// walk each warning and decide either RESTORE the callsite that references
// the var, OR delete the var. Do NOT bulk-delete without review — several
// of these signal features that were half-migrated.
import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  Eye, Plus, Trash2, Search, Youtube, Link2, Upload, X, ChevronUp, Video
} from 'lucide-react';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from '../components/ui/dialog';
// eslint-disable-next-line no-unused-vars -- FLAGGED (2026-02): ImageUploadButton
// was wired for VFX reference-shot upload but the JSX callsite was dropped.
// Restore <ImageUploadButton .../> in the shot card OR delete this import.
import { ImageUploadButton } from '../components/ImageUpload';
import { ImageLightbox } from '../components/ImageLightbox';
import { AuthImage } from '../components/AuthImage';
import { toast } from 'sonner';
import { MLText } from '../components/MLText';
import { getBreakdownItemLabel } from '../utils/breakdownLabel';
import { formatSceneNumber } from '../utils/sceneNumber';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── YouTube helpers ────────────────────────────────────────────────────────────
function getYouTubeId(url) {
  if (!url) return null;
  const m = url.match(/(?:youtu\.be\/|youtube\.com(?:\/embed\/|\/v\/|\/watch\?v=|\/watch\?.+&v=))([\w-]{11})/);
  return m ? m[1] : null;
}

function YTThumb({ url, onRemove }) {
  const [expanded, setExpanded] = useState(false);
  const vid = getYouTubeId(url);
  if (!vid) return (
    <div className="flex items-center gap-2 bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg px-3 py-2 group/link">
      <Video className="h-3.5 w-3.5 text-[#555] shrink-0" />
      <a href={url} target="_blank" rel="noreferrer" className="text-xs text-blue-400 hover:underline truncate flex-1">{url}</a>
      <button onClick={onRemove} className="opacity-0 group-hover/link:opacity-100 text-[#555] hover:text-red-400 transition-all">
        <X className="h-3 w-3" />
      </button>
    </div>
  );
  return (
    <div className="rounded-lg overflow-hidden border border-[#2a2a2a] group/yt">
      {!expanded ? (
        <div className="relative cursor-pointer" onClick={() => setExpanded(true)}>
          <img
            src={`https://img.youtube.com/vi/${vid}/mqdefault.jpg`}
            alt="Video thumbnail"
            className="w-full h-auto"
          />
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center hover:bg-black/20 transition-colors">
            <div className="h-10 w-10 bg-red-600 rounded-full flex items-center justify-center">
              <svg className="h-5 w-5 text-white ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z" />
              </svg>
            </div>
          </div>
          <button
            onClick={e => { e.stopPropagation(); onRemove(); }}
            className="absolute top-1.5 right-1.5 h-6 w-6 bg-black/70 rounded-full flex items-center justify-center opacity-0 group-hover/yt:opacity-100 transition-all hover:bg-red-600"
          >
            <X className="h-3 w-3 text-white" />
          </button>
        </div>
      ) : (
        <div>
          <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
            <iframe
              className="absolute inset-0 w-full h-full"
              src={`https://www.youtube.com/embed/${vid}?autoplay=1`}
              title="VFX reference video"
              allow="autoplay; encrypted-media"
              allowFullScreen
            />
          </div>
          <button
            onClick={() => setExpanded(false)}
            className="flex items-center gap-1 text-xs text-[#666] hover:text-[#f0f0f0] px-2 py-1.5 transition-colors"
          >
            <ChevronUp className="h-3 w-3" /> Collapse
          </button>
        </div>
      )}
    </div>
  );
}

// ── VFX Item ───────────────────────────────────────────────────────────────────
function VFXItemRow({ item, onDelete, onRefresh, token }) {
  const [lightbox, setLightbox] = useState(null);
  const [addingVideo, setAddingVideo] = useState(false);
  const [addingLink, setAddingLink] = useState(false);
  const [videoInput, setVideoInput] = useState('');
  const [linkInput, setLinkInput] = useState('');
  const [saving, setSaving] = useState(false);
  const photoInputRef = useRef();

  const videoRefs = item.video_refs || (item.youtube_url ? [item.youtube_url] : []);
  const linkRefs = item.link_refs || [];
  const images = (item.image_paths || []).map(p => ({ path: p, alt: item.description }));

  const patchItem = async (updates) => {
    setSaving(true);
    try {
      await axios.patch(`${API}/breakdown-items/${item.id}`, updates,
        { headers: { Authorization: `Bearer ${token}` } });
      onRefresh();
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const addVideo = async () => {
    if (!videoInput.trim()) return;
    await patchItem({ video_refs: [...videoRefs, videoInput.trim()] });
    setVideoInput(''); setAddingVideo(false);
  };

  const removeVideo = async (url) => {
    await patchItem({ video_refs: videoRefs.filter(v => v !== url) });
  };

  const addLink = async () => {
    if (!linkInput.trim()) return;
    await patchItem({ link_refs: [...linkRefs, linkInput.trim()] });
    setLinkInput(''); setAddingLink(false);
  };

  const removeLink = async (url) => {
    await patchItem({ link_refs: linkRefs.filter(l => l !== url) });
  };

  const handlePhotoUpload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      await axios.post(`${API}/breakdown-items/${item.id}/images`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      onRefresh();
    } catch { toast.error('Upload failed'); }
  };

  const deletePhoto = async (path) => {
    try {
      await axios.delete(`${API}/breakdown-items/${item.id}/images`,
        { headers: { Authorization: `Bearer ${token}` }, data: { path } });
      onRefresh();
    } catch { toast.error('Failed to remove'); }
  };

  return (
    <div className="bg-[#0d0d0d] rounded-xl p-3 group/vfx" data-testid={`vfx-item-${item.id}`}>
      {/* Header row */}
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-start gap-2 flex-1 min-w-0">
          <Eye className="h-4 w-4 text-cyan-400 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <MLText as="div" className="text-sm text-[#f0f0f0] font-medium">{getBreakdownItemLabel(item.description, 'original')}</MLText>
            {item.notes && <div className="text-xs text-[#a0a0a0] mt-0.5">{item.notes}</div>}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={() => setAddingVideo(v => !v)}
            className="h-6 w-6 rounded flex items-center justify-center text-[#555] hover:text-red-400 opacity-0 group-hover/vfx:opacity-100 transition-all"
            title="Add video link"
            data-testid={`vfx-add-video-${item.id}`}
          >
            <Youtube className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={() => setAddingLink(v => !v)}
            className="h-6 w-6 rounded flex items-center justify-center text-[#555] hover:text-blue-400 opacity-0 group-hover/vfx:opacity-100 transition-all"
            title="Add link"
            data-testid={`vfx-add-link-${item.id}`}
          >
            <Link2 className="h-3.5 w-3.5" />
          </button>
          <input ref={photoInputRef} type="file" accept="image/*" className="hidden"
            onChange={e => handlePhotoUpload(e.target.files?.[0])} />
          <button
            onClick={() => photoInputRef.current?.click()}
            className="h-6 w-6 rounded flex items-center justify-center text-[#555] hover:text-[#e8c547] opacity-0 group-hover/vfx:opacity-100 transition-all"
            title="Add photo"
          >
            <Upload className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={() => onDelete(item.id)}
            className="h-6 w-6 rounded flex items-center justify-center text-[#555] hover:text-red-400 opacity-0 group-hover/vfx:opacity-100 transition-all"
            data-testid={`delete-vfx-${item.id}`}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* Video thumbnails */}
      {videoRefs.length > 0 && (
        <div className="grid grid-cols-2 gap-2 mb-2">
          {videoRefs.map((url, i) => (
            <YTThumb key={`video-${url}-${i}`} url={url} onRemove={() => removeVideo(url)} />
          ))}
        </div>
      )}

      {/* Add video input */}
      {addingVideo && (
        <div className="flex gap-2 mb-2">
          <Input value={videoInput} onChange={e => setVideoInput(e.target.value)}
            placeholder="YouTube or video URL..."
            className="bg-[#1a1a1a] border-[#333] text-[#f0f0f0] text-xs h-7 flex-1"
            onKeyDown={e => e.key === 'Enter' && addVideo()}
            data-testid={`vfx-video-input-${item.id}`}
          />
          <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white h-7 text-xs px-2" onClick={addVideo} disabled={saving}>Add</Button>
          <button onClick={() => { setAddingVideo(false); setVideoInput(''); }} className="text-[#555] hover:text-[#f0f0f0]"><X className="h-3.5 w-3.5" /></button>
        </div>
      )}

      {/* Reference links */}
      {linkRefs.length > 0 && (
        <div className="space-y-1 mb-2">
          {linkRefs.map((url, i) => (
            <div key={`link-${url}-${i}`} className="flex items-center gap-2 bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg px-3 py-1.5 group/link">
              <Link2 className="h-3 w-3 text-blue-400 shrink-0" />
              <a href={url} target="_blank" rel="noreferrer" className="text-xs text-blue-400 hover:underline truncate flex-1">{url}</a>
              <button onClick={() => removeLink(url)} className="opacity-0 group-hover/link:opacity-100 text-[#555] hover:text-red-400 transition-all">
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Add link input */}
      {addingLink && (
        <div className="flex gap-2 mb-2">
          <Input value={linkInput} onChange={e => setLinkInput(e.target.value)}
            placeholder="https://reference-link.com..."
            className="bg-[#1a1a1a] border-[#333] text-[#f0f0f0] text-xs h-7 flex-1"
            onKeyDown={e => e.key === 'Enter' && addLink()}
            data-testid={`vfx-link-input-${item.id}`}
          />
          <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white h-7 text-xs px-2" onClick={addLink} disabled={saving}>Add</Button>
          <button onClick={() => { setAddingLink(false); setLinkInput(''); }} className="text-[#555] hover:text-[#f0f0f0]"><X className="h-3.5 w-3.5" /></button>
        </div>
      )}

      {/* Photo thumbnails with delete */}
      {images.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {images.map((img, i) => (
            <div key={img.alt || `img-${i}`} className="relative group/ph h-16 w-16 rounded overflow-hidden border border-[#2a2a2a]">
              <AuthImage path={img.path} alt={img.alt} className="h-full w-full object-cover cursor-pointer"
                onClick={() => setLightbox({ images, idx: i })} />
              <button
                onClick={() => deletePhoto(item.image_paths[i])}
                className="absolute inset-0 bg-black/60 opacity-0 group-hover/ph:opacity-100 flex items-center justify-center transition-opacity"
                data-testid={`vfx-delete-photo-${item.id}-${i}`}
              >
                <X className="h-4 w-4 text-white" />
              </button>
            </div>
          ))}
        </div>
      )}

      {lightbox && <ImageLightbox images={lightbox.images} initialIndex={lightbox.idx} onClose={() => setLightbox(null)} />}
    </div>
  );
}


// ── Main Page ─────────────────────────────────────────────────────────────────
export default function VFXDeptPage() {
  const { projectId } = useParams();
  const { token } = useAuth();
  const [allScenes, setAllScenes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [dialogSceneId, setDialogSceneId] = useState('');
  const [vfxName, setVfxName] = useState('');
  const [vfxNotes, setVfxNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  // projectId is the only stable dependency needed here; fetchData is defined below
  // and would change on every render if included without useCallback.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData(); }, [projectId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/projects/${projectId}/breakdown`, { headers });
      setAllScenes(res.data);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  const vfxScenes = allScenes.filter(s => (s.breakdown_items?.VFX || []).length > 0);
  const totalVFX = vfxScenes.reduce((s, sc) => s + (sc.breakdown_items?.VFX || []).length, 0);

  const filteredVfxScenes = search
    ? vfxScenes.filter(s =>
        String(s.scene_number ?? '').includes(search) ||
        s.location_name?.toLowerCase().includes(search.toLowerCase()) ||
        (s.breakdown_items?.VFX || []).some(v => v.description?.toLowerCase().includes(search.toLowerCase()))
      )
    : vfxScenes;

  const handleAdd = async () => {
    if (!vfxName.trim()) { toast.error('Description required'); return; }
    if (!dialogSceneId) { toast.error('Select a scene'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/projects/${projectId}/breakdown-items`, {
        scene_id: dialogSceneId, category: 'VFX',
        description: vfxName, notes: vfxNotes || null,
        video_refs: [], link_refs: [],
      }, { headers });
      toast.success('VFX shot added');
      setShowDialog(false); setVfxName(''); setVfxNotes(''); setDialogSceneId('');
      fetchData();
    } catch { toast.error('Failed to add'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (itemId) => {
    try {
      await axios.delete(`${API}/breakdown-items/${itemId}`, { headers });
      fetchData();
    } catch { toast.error('Failed'); }
  };

  return (
    <div className="min-h-screen bg-[#0d0d0d] text-[#f0f0f0]">
      <div className="border-b border-[#2a2a2a] px-6 py-5">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-['DM_Serif_Display'] text-2xl">VFX</h1>
            <p className="text-sm text-[#a0a0a0] mt-0.5">Visual effects shots — video references, photos, links per scene.</p>
          </div>
          <div className="flex items-center gap-5">
            <div className="text-center">
              <div className="text-xl font-bold text-cyan-400" data-testid="vfx-total-count">{totalVFX}</div>
              <div className="text-xs text-[#a0a0a0]">VFX Shots</div>
            </div>
            <div className="text-center">
              <div className="text-xl font-bold text-[#e8c547]">{vfxScenes.length}</div>
              <div className="text-xs text-[#a0a0a0]">Scenes</div>
            </div>
            <Button size="sm" className="bg-cyan-600 hover:bg-cyan-700 text-white"
              onClick={() => { setDialogSceneId(''); setVfxName(''); setVfxNotes(''); setShowDialog(true); }}
              data-testid="add-vfx-global-btn">
              <Plus className="h-4 w-4 mr-1" /> Add VFX Shot
            </Button>
          </div>
        </div>
      </div>

      {loading ? <div className="text-center py-12 text-[#a0a0a0]">Loading...</div> : (
        <div className="p-6">
          <div className="relative mb-6 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#a0a0a0]" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Filter VFX scenes..."
              className="pl-10 bg-[#1a1a1a] border-[#2a2a2a] text-[#f0f0f0]" data-testid="vfx-search" />
          </div>

          {vfxScenes.length === 0 ? (
            <Card className="bg-[#1a1a1a] border-[#2a2a2a] p-12 text-center">
              <Eye className="h-12 w-12 text-[#2a2a2a] mx-auto mb-3" />
              <p className="text-[#a0a0a0]">No VFX shots yet. Click "Add VFX Shot" to begin.</p>
            </Card>
          ) : (
            <div className="space-y-4">
              {filteredVfxScenes.map(scene => {
                const items = scene.breakdown_items?.VFX || [];
                return (
                  <Card key={scene.id} className="bg-[#1a1a1a] border-[#2a2a2a] overflow-hidden" data-testid={`vfx-scene-${scene.scene_number ?? 'unnumbered'}`}>
                    <div className="flex items-center justify-between px-4 py-3 border-b border-[#2a2a2a]">
                      <div className="flex items-center gap-3">
                        <span className="text-xs font-bold text-[#e8c547] bg-[#2a2a2a] h-7 w-7 rounded flex items-center justify-center">{formatSceneNumber(scene.scene_number)}</span>
                        <span className="font-medium text-[#f0f0f0] text-sm">{scene.scene_header}</span>
                        <Badge className="bg-cyan-500/20 text-cyan-400 border-0 text-xs">{items.length} VFX</Badge>
                      </div>
                      <Button size="sm" variant="outline"
                        className="border-[#2a2a2a] text-[#f0f0f0] hover:bg-[#0d0d0d] h-7 text-xs"
                        onClick={() => { setDialogSceneId(scene.id); setVfxName(''); setVfxNotes(''); setShowDialog(true); }}
                        data-testid={`add-vfx-${scene.scene_number ?? 'unnumbered'}`}>
                        <Plus className="h-3 w-3 mr-1" /> Add
                      </Button>
                    </div>
                    <div className="p-4 space-y-2">
                      {items.map(item => (
                        <VFXItemRow key={item.id} item={item} token={token} onDelete={handleDelete} onRefresh={fetchData} />
                      ))}
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-[#1a1a1a] border-[#2a2a2a] text-[#f0f0f0]">
          <DialogHeader>
            <DialogTitle className="font-['DM_Serif_Display'] text-xl">Add VFX Shot</DialogTitle>
            <DialogDescription className="sr-only">Add a new VFX shot to a scene</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs text-[#a0a0a0] mb-1 block">Scene *</label>
              <Select value={dialogSceneId} onValueChange={setDialogSceneId}>
                <SelectTrigger className="bg-[#0d0d0d] border-[#2a2a2a] text-[#f0f0f0]" data-testid="vfx-scene-select">
                  <SelectValue placeholder="Select a scene..." />
                </SelectTrigger>
                <SelectContent className="bg-[#1a1a1a] border-[#2a2a2a] text-[#f0f0f0] max-h-60">
                  {allScenes.map(s => (
                    <SelectItem key={s.id} value={s.id} className="text-[#f0f0f0]">
                      Sc {formatSceneNumber(s.scene_number)} — {s.scene_header?.slice(0, 50)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-[#a0a0a0] mb-1 block">VFX Description *</label>
              <Input value={vfxName} onChange={e => setVfxName(e.target.value)}
                placeholder="e.g. Digital cityscape background replacement"
                className="bg-[#0d0d0d] border-[#2a2a2a] text-[#f0f0f0]" data-testid="vfx-name-input" />
            </div>
            <div>
              <label className="text-xs text-[#a0a0a0] mb-1 block">Notes</label>
              <Input value={vfxNotes} onChange={e => setVfxNotes(e.target.value)}
                placeholder="Additional technical notes"
                className="bg-[#0d0d0d] border-[#2a2a2a] text-[#f0f0f0]" />
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowDialog(false)} className="border-[#2a2a2a] text-[#f0f0f0]">Cancel</Button>
              <Button onClick={handleAdd} disabled={saving} className="bg-[#e8c547] hover:bg-[#d6b539] text-black" data-testid="add-vfx-btn">
                {saving ? 'Adding...' : 'Add VFX Shot'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
